import React, { useState, useRef } from 'react';
import { Plus, Trash2, Edit2, Calendar, AlertCircle, TrendingUp, Lock, Zap, GripVertical, Share2 } from 'lucide-react';
import { formatNumber, getTotalPoints, getTotalCosts, getTotalFees, parseFormattedNumber, formatNumberInput } from '../../utils';
import { AirdropShareModal } from '../AirdropShareModal';

const AIRDROP_TEMPLATES = [
  {
    id: 'extended',
    name: 'Extended',
    description: 'Trading protocol',
    data: {
      name: 'Extended',
      type: 'Trading',
      tge: 'H1 2026',
      capital: '',
      startDate: new Date().toISOString().split('T')[0],
      priority: 'High',
      status: 'Active',
      notes: 'Farm points every Tuesday. Focus on trading volume.',
      metricType: 'points',
      customMetricName: '',
      roiScenarios: { bearish: 3, base: 5, bullish: 10 }
    }
  },
  {
    id: 'hibachi',
    name: 'Hibachi',
    description: 'Double points in March',
    data: {
      name: 'Hibachi',
      type: 'Trading',
      tge: 'Q2 2026',
      capital: '',
      startDate: new Date().toISOString().split('T')[0],
      priority: 'High',
      status: 'Active',
      notes: 'Double points active in March. Use referral link for bonus.',
      metricType: 'points',
      customMetricName: '',
      roiScenarios: { bearish: 0.5, base: 0.75, bullish: 1 }
    }
  },
  {
    id: 'variational',
    name: 'Variational',
    description: 'Trading protocol',
    data: {
      name: 'Variational',
      type: 'Trading',
      tge: 'Q3 2026',
      capital: '',
      startDate: new Date().toISOString().split('T')[0],
      priority: 'Medium',
      status: 'Active',
      notes: 'Points calculated internally. Focus on daily activity.',
      metricType: 'points',
      customMetricName: '',
      roiScenarios: { bearish: 20, base: 45, bullish: 90 }
    }
  },
  {
    id: 'predictfun',
    name: 'Predict Fun',
    description: 'Prediction markets',
    data: {
      name: 'Predict Fun',
      type: 'Prediction Markets',
      tge: 'TBD',
      capital: '',
      startDate: new Date().toISOString().split('T')[0],
      priority: 'Medium',
      status: 'Active',
      notes: 'Farm points every Thursday. Make predictions consistently.',
      metricType: 'points',
      customMetricName: '',
      roiScenarios: { bearish: 0.2, base: 0.5, bullish: 1 }
    }
  }
];

const MobileCard = ({ projectName, projectSettings, editingProject, editForm, setEditForm,
  cancelEdit, saveEdit, startEdit, deleteAirdrop, setSharingProject, weeklyData, isDark, colors,
  inputClasses, TYPE_OPTIONS, METRIC_OPTIONS, PRIORITY_OPTIONS, STATUS_OPTIONS,
  handleNumberChange, handleNumberBlur, getMetricLabel, getMetricValue, getTotalCosts,
  getExpectedROI, getStatusColor, getPriorityColor, getTypeColor, formatNumber }) => {

  const s = projectSettings[projectName] || {};
  const isEditing = editingProject === projectName;
  const metricType = s.metricType || 'points';
  const metricLabel = getMetricLabel(metricType, s.customMetricName);
  const metricValue = getMetricValue(projectName, metricType);
  const costs = getTotalCosts(weeklyData, projectName);
  const expectedROI = getExpectedROI(projectName);
  const ingresos = s.ingresos || 0;
  const pnl = ingresos - costs;

  return (
    <div style={{ scrollSnapAlign: 'start', minWidth: '85vw', maxWidth: '85vw' }}
      className={`rounded-2xl p-5 border flex-shrink-0 ${isDark ? 'bg-slate-700/50 border-slate-600' : 'bg-white border-gray-200'} shadow-lg overflow-y-auto`}>

      {isEditing ? (
        <>
          <div className="flex items-center justify-between mb-4">
            <h4 className={`font-bold text-lg ${colors.textPrimary}`}>Edit {projectName}</h4>
            <button onClick={cancelEdit} className={`text-xs px-3 py-1 rounded-lg ${isDark ? 'bg-slate-600 text-white' : 'bg-gray-200 text-gray-700'}`}>Cancel</button>
          </div>
          <div className="space-y-3">
            <div><label className={`block text-xs mb-1 ${colors.textSecondary}`}>Name</label><input type="text" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className={inputClasses} /></div>
            <div><label className={`block text-xs mb-1 ${colors.textSecondary}`}>Type</label><select value={editForm.type} onChange={(e) => setEditForm({ ...editForm, type: e.target.value })} className={inputClasses}>{TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</select></div>
            <div><label className={`block text-xs mb-1 ${colors.textSecondary}`}>Metric</label><select value={editForm.metricType} onChange={(e) => setEditForm({ ...editForm, metricType: e.target.value })} className={inputClasses}>{METRIC_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</select></div>
            {editForm.metricType === 'custom' && <div><label className={`block text-xs mb-1 ${colors.textSecondary}`}>Custom Metric Name</label><input type="text" value={editForm.customMetricName} onChange={(e) => setEditForm({ ...editForm, customMetricName: e.target.value })} className={inputClasses} /></div>}
            <div><label className={`block text-xs mb-1 ${colors.textSecondary}`}>Capital ($)</label><input type="text" value={editForm.capital} onChange={(e) => handleNumberChange(setEditForm, editForm, 'capital', e.target.value)} onBlur={() => handleNumberBlur(setEditForm, editForm, 'capital')} className={inputClasses} /></div>
            <div><label className={`block text-xs mb-1 ${colors.textSecondary}`}>TGE</label><input type="text" value={editForm.tge} onChange={(e) => setEditForm({ ...editForm, tge: e.target.value })} className={inputClasses} /></div>
            <div><label className={`block text-xs mb-1 ${colors.textSecondary}`}>Start Date</label><input type="date" value={editForm.startDate} onChange={(e) => setEditForm({ ...editForm, startDate: e.target.value })} className={inputClasses} /></div>
            <div><label className={`block text-xs mb-1 ${colors.textSecondary}`}>Priority</label><select value={editForm.priority} onChange={(e) => setEditForm({ ...editForm, priority: e.target.value })} className={inputClasses}>{PRIORITY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</select></div>
            <div><label className={`block text-xs mb-1 ${colors.textSecondary}`}>Status</label><select value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })} className={inputClasses}>{STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</select></div>
            <div><label className={`block text-xs mb-1 ${colors.textSecondary}`}>Notes</label><textarea value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} rows="2" className={inputClasses} /></div>
            <div><label className={`block text-xs mb-1 ${colors.textSecondary}`}>Ingresos ($)</label><input type="text" value={editForm.ingresos} onChange={(e) => handleNumberChange(setEditForm, editForm, 'ingresos', e.target.value)} onBlur={() => handleNumberBlur(setEditForm, editForm, 'ingresos')} className={inputClasses} /></div>
            <button onClick={saveEdit} className="w-full bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg font-semibold transition-colors">Save Changes</button>
          </div>
        </>
      ) : (
        <>
          <div className="flex items-start justify-between mb-4">
            <div>
              <h4 className={`text-xl font-bold mb-1 ${colors.textPrimary}`}>{projectName}</h4>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`px-2 py-0.5 rounded text-xs font-semibold border ${getStatusColor(s.status || 'Active')}`}>{s.status || 'Active'}</span>
                <span className={`px-2 py-0.5 rounded text-xs font-semibold border ${getPriorityColor(s.priority || 'Medium')}`}>{s.priority || 'Medium'}</span>
                <span className={`px-2 py-0.5 rounded text-xs ${getTypeColor(s.type)}`}>{s.type || 'Trading'}</span>
              </div>
            </div>
            <div className="flex gap-1">
              <button onClick={() => setSharingProject(projectName)} className="text-green-400 hover:text-green-300 p-1.5"><Share2 size={16} /></button>
              <button onClick={() => startEdit(projectName)} className="text-blue-400 hover:text-blue-300 p-1.5"><Edit2 size={16} /></button>
              <button onClick={() => deleteAirdrop(projectName)} className="text-red-400 hover:text-red-300 p-1.5"><Trash2 size={16} /></button>
            </div>
          </div>

          {s.tge && (
            <div className={`flex items-center gap-1.5 text-sm mb-4 ${colors.textSecondary}`}>
              <Calendar size={13} />
              <span>TGE: <strong className={colors.textPrimary}>{s.tge}</strong></span>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className={`rounded-xl p-3 ${isDark ? 'bg-slate-800/60' : 'bg-gray-50'}`}>
              <div className={`text-xs mb-1 ${colors.textSecondary}`}>Capital</div>
              <div className={`text-lg font-bold ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>${formatNumber(s.capital || 0)}</div>
            </div>
            <div className={`rounded-xl p-3 ${isDark ? 'bg-slate-800/60' : 'bg-gray-50'}`}>
              <div className={`text-xs mb-1 ${colors.textSecondary}`}>{metricLabel}</div>
              <div className={`text-lg font-bold ${isDark ? 'text-green-400' : 'text-green-600'}`}>{formatNumber(metricValue)}</div>
            </div>
            <div className={`rounded-xl p-3 ${isDark ? 'bg-slate-800/60' : 'bg-gray-50'}`}>
              <div className={`text-xs mb-1 ${colors.textSecondary}`}>Costes</div>
              <div className={`text-lg font-bold ${isDark ? 'text-red-400' : 'text-red-600'}`}>${formatNumber(costs)}</div>
            </div>
            <div className={`rounded-xl p-3 ${isDark ? 'bg-slate-800/60' : 'bg-gray-50'}`}>
              <div className={`text-xs mb-1 ${colors.textSecondary}`}>Ingresos</div>
              <div className={`text-lg font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>+${formatNumber(ingresos)}</div>
            </div>
          </div>

          <div className={`rounded-xl p-3 mb-3 ${pnl >= 0 ? (isDark ? 'bg-green-900/20 border border-green-700/30' : 'bg-green-50 border border-green-100') : (isDark ? 'bg-red-900/20 border border-red-700/30' : 'bg-red-50 border border-red-100')}`}>
            <div className={`text-xs mb-1 ${colors.textSecondary}`}>PnL neto</div>
            <div className={`text-xl font-bold ${pnl >= 0 ? (isDark ? 'text-green-400' : 'text-green-600') : (isDark ? 'text-red-400' : 'text-red-600')}`}>
              {pnl >= 0 ? '+' : ''}{formatNumber(pnl)}$
            </div>
          </div>

          <div className={`rounded-xl p-3 ${isDark ? 'bg-blue-900/20 border border-blue-700/30' : 'bg-blue-50 border border-blue-100'}`}>
            <div className={`text-xs mb-1 flex items-center gap-1 ${colors.textSecondary}`}><TrendingUp size={12} />Expected ROI</div>
            <div className={`text-xl font-bold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{expectedROI ? `$${formatNumber(expectedROI)}` : '—'}</div>
          </div>

          {s.notes && <div className={`mt-3 text-xs italic rounded-lg p-2 ${isDark ? 'bg-slate-800/30 text-slate-400' : 'bg-gray-100 text-gray-500'}`}>💡 {s.notes}</div>}
        </>
      )}
    </div>
  );
};

const DesktopCard = ({ projectName, projectSettings, editingProject, editForm, setEditForm,
  cancelEdit, saveEdit, startEdit, deleteAirdrop, setSharingProject, weeklyData, isDark, colors,
  inputClasses, TYPE_OPTIONS, METRIC_OPTIONS, PRIORITY_OPTIONS, STATUS_OPTIONS,
  handleNumberChange, handleNumberBlur, getMetricLabel, getMetricValue, getTotalCosts,
  getExpectedROI, getStatusColor, getPriorityColor, getTypeColor, formatNumber,
  isDragging, isDraggingOver }) => {

  const s = projectSettings[projectName] || {};
  const isEditing = editingProject === projectName;
  const metricType = s.metricType || 'points';
  const metricLabel = getMetricLabel(metricType, s.customMetricName);
  const metricValue = getMetricValue(projectName, metricType);
  const costs = getTotalCosts(weeklyData, projectName);
  const expectedROI = getExpectedROI(projectName);
  const ingresos = s.ingresos || 0;
  const pnl = ingresos - costs;

  return (
    <div className={`rounded-lg p-5 border transition-all ${
      isDragging ? 'opacity-40 scale-95' : isDraggingOver
        ? (isDark ? 'border-blue-500 bg-blue-900/10' : 'border-blue-400 bg-blue-50')
        : (isDark ? 'bg-slate-700/30 border-slate-600' : 'bg-gray-50 border-gray-200')
    }`}>
      {isEditing ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div><label className={`block text-sm mb-2 ${colors.textSecondary}`}>Name</label><input type="text" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className={inputClasses} /></div>
            <div><label className={`block text-sm mb-2 ${colors.textSecondary}`}>Type</label><select value={editForm.type} onChange={(e) => setEditForm({ ...editForm, type: e.target.value })} className={inputClasses}>{TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</select></div>
          </div>
          <div className={`rounded-lg p-4 mb-4 border ${isDark ? 'bg-purple-900/20 border-purple-700/30' : 'bg-purple-50 border-purple-200'}`}>
            <h5 className={`font-semibold mb-3 ${colors.textPrimary}`}>📏 Metric</h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><label className={`block text-sm mb-2 ${colors.textSecondary}`}>Main Metric</label><select value={editForm.metricType} onChange={(e) => setEditForm({ ...editForm, metricType: e.target.value })} className={inputClasses}>{METRIC_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</select></div>
              {editForm.metricType === 'custom' && <div><label className={`block text-sm mb-2 ${colors.textSecondary}`}>Custom Metric Name</label><input type="text" value={editForm.customMetricName} onChange={(e) => setEditForm({ ...editForm, customMetricName: e.target.value })} className={inputClasses} /></div>}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div><label className={`block text-sm mb-2 ${colors.textSecondary}`}>Capital ($)</label><input type="text" value={editForm.capital} onChange={(e) => handleNumberChange(setEditForm, editForm, 'capital', e.target.value)} onBlur={() => handleNumberBlur(setEditForm, editForm, 'capital')} className={inputClasses} /></div>
            <div><label className={`block text-sm mb-2 ${colors.textSecondary}`}>TGE</label><input type="text" value={editForm.tge} onChange={(e) => setEditForm({ ...editForm, tge: e.target.value })} className={inputClasses} /></div>
            <div><label className={`block text-sm mb-2 ${colors.textSecondary}`}>Start Date</label><input type="date" value={editForm.startDate} onChange={(e) => setEditForm({ ...editForm, startDate: e.target.value })} className={inputClasses} /></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div><label className={`block text-sm mb-2 ${colors.textSecondary}`}>Priority</label><select value={editForm.priority} onChange={(e) => setEditForm({ ...editForm, priority: e.target.value })} className={inputClasses}>{PRIORITY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</select></div>
            <div><label className={`block text-sm mb-2 ${colors.textSecondary}`}>Status</label><select value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })} className={inputClasses}>{STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</select></div>
          </div>
          <div className="mb-4"><label className={`block text-sm mb-2 ${colors.textSecondary}`}>Notes</label><textarea value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} rows="2" className={inputClasses} /></div>
          <div className="mb-4"><label className={`block text-sm mb-2 ${colors.textSecondary}`}>Ingresos ($) <span className={`font-normal opacity-60`}>— reducen el coste neto del airdrop</span></label><input type="text" value={editForm.ingresos} onChange={(e) => handleNumberChange(setEditForm, editForm, 'ingresos', e.target.value)} onBlur={() => handleNumberBlur(setEditForm, editForm, 'ingresos')} placeholder="0" className={inputClasses} /></div>
          <div className="flex gap-2">
            <button onClick={saveEdit} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors">Save</button>
            <button onClick={cancelEdit} className={`px-4 py-2 rounded-lg transition-colors ${isDark ? 'bg-slate-600 hover:bg-slate-700 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}>Cancel</button>
          </div>
        </>
      ) : (
        <>
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-start gap-3 flex-1">
              <div className={`cursor-grab active:cursor-grabbing mt-1 ${colors.textSecondary} opacity-40 hover:opacity-80 transition-opacity`}><GripVertical size={18} /></div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2 flex-wrap">
                  <h4 className={`text-xl font-bold ${colors.textPrimary}`}>{projectName}</h4>
                  <span className={`px-2 py-1 rounded text-xs font-semibold border ${getStatusColor(s.status || 'Active')}`}>{s.status || 'Active'}</span>
                  <span className={`px-2 py-1 rounded text-xs font-semibold border ${getPriorityColor(s.priority || 'Medium')}`}>{s.priority || 'Medium'}</span>
                </div>
                <div className={`flex items-center gap-4 text-sm ${colors.textSecondary}`}>
                  <span className={`px-2 py-0.5 rounded text-xs ${getTypeColor(s.type)}`}>{s.type || 'Trading'}</span>
                  {s.tge && <span className="flex items-center gap-1"><Calendar size={14} />TGE: {s.tge}</span>}
                </div>
                {s.notes && <div className={`mt-2 text-sm italic rounded p-2 ${isDark ? 'bg-slate-800/30 text-slate-400' : 'bg-gray-100 text-gray-600'}`}>💡 {s.notes}</div>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setSharingProject(projectName)} className="text-green-400 hover:text-green-300 p-2"><Share2 size={18} /></button>
              <button onClick={() => startEdit(projectName)} className="text-blue-400 hover:text-blue-300 p-2"><Edit2 size={18} /></button>
              <button onClick={() => deleteAirdrop(projectName)} className="text-red-400 hover:text-red-300 p-2"><Trash2 size={18} /></button>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-7 gap-3">
            <div className={`rounded p-3 ${isDark ? 'bg-slate-800/50' : 'bg-gray-100'}`}><div className={`text-xs mb-1 ${colors.textSecondary}`}>Capital</div><div className={`text-lg font-bold ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>${formatNumber(s.capital || 0)}</div></div>
            <div className={`rounded p-3 ${isDark ? 'bg-slate-800/50' : 'bg-gray-100'}`}><div className={`text-xs mb-1 ${colors.textSecondary}`}>{metricLabel}</div><div className={`text-lg font-bold ${isDark ? 'text-green-400' : 'text-green-600'}`}>{formatNumber(metricValue)}</div></div>
            <div className={`rounded p-3 ${isDark ? 'bg-slate-800/50' : 'bg-gray-100'}`}><div className={`text-xs mb-1 ${colors.textSecondary}`}>Costes</div><div className={`text-lg font-bold ${isDark ? 'text-red-400' : 'text-red-600'}`}>${formatNumber(costs)}</div></div>
            <div className={`rounded p-3 ${isDark ? 'bg-slate-800/50' : 'bg-gray-100'}`}><div className={`text-xs mb-1 ${colors.textSecondary}`}>Ingresos</div><div className={`text-lg font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>+${formatNumber(ingresos)}</div></div>
            <div className={`rounded p-3 ${isDark ? 'bg-slate-800/50' : 'bg-gray-100'}`}><div className={`text-xs mb-1 ${colors.textSecondary}`}>PnL neto</div><div className={`text-lg font-bold ${pnl >= 0 ? (isDark ? 'text-green-400' : 'text-green-600') : (isDark ? 'text-red-400' : 'text-red-600')}`}>{pnl >= 0 ? '+' : ''}{formatNumber(pnl)}$</div></div>
            <div className={`rounded p-3 ${isDark ? 'bg-slate-800/50' : 'bg-gray-100'}`}><div className={`text-xs mb-1 ${colors.textSecondary}`}>$/{metricLabel}</div><div className={`text-lg font-bold ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`}>${metricValue > 0 ? (costs / metricValue).toFixed(2) : '0.00'}</div></div>
            <div className={`rounded p-3 ${isDark ? 'bg-slate-800/50' : 'bg-gray-100'}`}><div className={`text-xs mb-1 flex items-center gap-1 ${colors.textSecondary}`}><TrendingUp size={12} />Expected ROI</div><div className={`text-lg font-bold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{expectedROI ? `$${formatNumber(expectedROI)}` : '—'}</div></div>
          </div>
        </>
      )}
    </div>
  );
};

export const ProjectsTab = ({
  weeklyData = {},
  projectSettings = {},
  setProjectSettings,
  calendarEvents = [],
  setCalendarEvents,
  isDark,
  colors,
  isPro,
  openPricing
}) => {
  const [editingProject, setEditingProject] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [showTemplates, setShowTemplates] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [projectOrder, setProjectOrder] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);
  const [sharingProject, setSharingProject] = useState(null);
  const [newAirdrop, setNewAirdrop] = useState({
    name: '', type: 'Trading', tge: '', capital: '',
    startDate: new Date().toISOString().split('T')[0],
    priority: 'Medium', status: 'Active', notes: '', metricType: 'points', customMetricName: '', ingresos: ''
  });

  const TYPE_OPTIONS = [
    { value: 'Trading', label: 'Trading' },
    { value: 'Prediction Markets', label: 'Prediction Markets' },
    { value: 'DeFi', label: 'DeFi' },
    { value: 'NFT', label: 'NFT' },
    { value: 'Social', label: 'Social' },
    { value: 'Gaming', label: 'Gaming' }
  ];
  const METRIC_OPTIONS = [
    { value: 'points', label: 'Points' },
    { value: 'volume', label: 'Volume ($)' },
    { value: 'fees', label: 'Fees Earned ($)' },
    { value: 'custom', label: 'Custom Metric' }
  ];
  const PRIORITY_OPTIONS = [
    { value: 'High', label: '🔴 High' },
    { value: 'Medium', label: '🟡 Medium' },
    { value: 'Low', label: '🟢 Low' }
  ];
  const STATUS_OPTIONS = [
    { value: 'Active', label: '✅ Active' },
    { value: 'Paused', label: '⏸️ Paused' },
    { value: 'Completed', label: '✔️ Completed' }
  ];

  const FREE_AIRDROP_LIMIT = 3;
  const rawProjects = Object.keys(projectSettings);
  const allProjects = projectOrder
    ? projectOrder.filter(p => rawProjects.includes(p)).concat(rawProjects.filter(p => !projectOrder.includes(p)))
    : rawProjects;

  const getMetricLabel = (metricType, customName) => {
    if (metricType === 'custom' && customName) return customName;
    return METRIC_OPTIONS.find(m => m.value === metricType)?.label || 'Points';
  };

  const getTotalVolume = (wd, key) => {
    let t = 0;
    Object.values(wd?.weeks || {}).forEach(w => w.forEach(e => { if (e.project.toLowerCase().includes(key.toLowerCase())) t += e.volume || 0; }));
    return t;
  };
  const getTotalCustomMetric = (wd, key) => {
    let t = 0;
    Object.values(wd?.weeks || {}).forEach(w => w.forEach(e => { if (e.project.toLowerCase().includes(key.toLowerCase())) t += e.customMetric || 0; }));
    return t;
  };
  const getMetricValue = (name, type) => {
    switch (type) {
      case 'points': return getTotalPoints(weeklyData, name);
      case 'volume': return getTotalVolume(weeklyData, name);
      case 'fees': return getTotalFees(weeklyData, name);
      case 'custom': return getTotalCustomMetric(weeklyData, name);
      default: return getTotalPoints(weeklyData, name);
    }
  };

  const parseTGEtoDate = (tge) => {
    if (!tge || tge === 'TBD') return null;
    const year = tge.match(/\d{4}/)?.[0];
    if (!year) return null;
    if (tge.includes('Q1')) return `${year}-03-31`;
    if (tge.includes('Q2')) return `${year}-06-30`;
    if (tge.includes('Q3')) return `${year}-09-30`;
    if (tge.includes('Q4')) return `${year}-12-31`;
    if (tge.includes('H1')) return `${year}-06-30`;
    if (tge.includes('H2')) return `${year}-12-31`;
    return tge.includes('-') ? tge : `${year}-12-31`;
  };

  const handleNumberChange = (setter, obj, field, value) => setter({ ...obj, [field]: value.replace(/[^0-9.]/g, '') });
  const handleNumberBlur = (setter, obj, field) => {
    if (obj[field]) setter({ ...obj, [field]: formatNumberInput(parseFormattedNumber(obj[field])) });
  };

  const applyTemplate = (template) => {
    setSelectedTemplate(template.id);
    setNewAirdrop({ ...template.data, ingresos: '' });
    setShowTemplates(false);
  };
  const resetForm = () => {
    setSelectedTemplate(null);
    setShowTemplates(true);
    setNewAirdrop({ name: '', type: 'Trading', tge: '', capital: '', startDate: new Date().toISOString().split('T')[0], priority: 'Medium', status: 'Active', notes: '', metricType: 'points', customMetricName: '', ingresos: '' });
  };

  const addAirdrop = () => {
    if (!newAirdrop.name) { alert('Please enter a name'); return; }
    if (newAirdrop.metricType === 'custom' && !newAirdrop.customMetricName) { alert('Please enter custom metric name'); return; }
    if (!isPro && Object.keys(projectSettings).length >= FREE_AIRDROP_LIMIT) { if (openPricing) openPricing(); return; }
    const templateRoi = AIRDROP_TEMPLATES.find(t => t.id === selectedTemplate)?.data?.roiScenarios;
    const airdropData = {
      type: newAirdrop.type || 'Trading',
      capital: parseFormattedNumber(newAirdrop.capital),
      tge: newAirdrop.tge || 'TBD',
      startDate: newAirdrop.startDate || new Date().toISOString().split('T')[0],
      priority: newAirdrop.priority || 'Medium',
      status: newAirdrop.status || 'Active',
      notes: newAirdrop.notes || '',
      metricType: newAirdrop.metricType || 'points',
      customMetricName: newAirdrop.metricType === 'custom' ? newAirdrop.customMetricName : null,
      ingresos: parseFormattedNumber(newAirdrop.ingresos) || 0,
      active: true,
      ...(templateRoi && { roiScenarios: templateRoi })
    };
    setProjectSettings({ ...projectSettings, [newAirdrop.name]: airdropData });
    if (newAirdrop.tge && newAirdrop.tge !== 'TBD') {
      const tgeDate = parseTGEtoDate(newAirdrop.tge);
      if (tgeDate) setCalendarEvents([...calendarEvents, { id: Date.now(), title: `${newAirdrop.name} TGE`, date: tgeDate, type: 'tge', project: newAirdrop.name }]);
    }
    resetForm();
    setShowAddForm(false);
  };

  const startEdit = (name) => {
    const s = projectSettings[name];
    setEditingProject(name);
    setEditForm({ name, type: s.type || 'Trading', capital: formatNumberInput(s.capital || 0), tge: s.tge || '', startDate: s.startDate || '', priority: s.priority || 'Medium', status: s.status || 'Active', notes: s.notes || '', metricType: s.metricType || 'points', customMetricName: s.customMetricName || '', ingresos: formatNumberInput(s.ingresos || 0) });
  };
  const saveEdit = () => {
    if (editForm.metricType === 'custom' && !editForm.customMetricName) { alert('Please enter custom metric name'); return; }
    const updated = { ...projectSettings[editingProject], type: editForm.type, capital: parseFormattedNumber(editForm.capital), tge: editForm.tge, startDate: editForm.startDate, priority: editForm.priority, status: editForm.status, notes: editForm.notes, metricType: editForm.metricType, customMetricName: editForm.metricType === 'custom' ? editForm.customMetricName : null, ingresos: parseFormattedNumber(editForm.ingresos) || 0 };
    if (editForm.name !== editingProject) {
      const n = { ...projectSettings };
      delete n[editingProject];
      n[editForm.name] = updated;
      setProjectSettings(n);
    } else {
      setProjectSettings({ ...projectSettings, [editingProject]: updated });
    }
    setEditingProject(null); setEditForm({});
  };
  const cancelEdit = () => { setEditingProject(null); setEditForm({}); };
  const deleteAirdrop = (name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    const n = { ...projectSettings }; delete n[name];
    setProjectSettings(n);
    setCalendarEvents(calendarEvents.filter(ev => ev.project !== name));
  };

  const handleDragStart = (e, name) => {
    setDraggedItem(name);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', name);
  };
  const handleDragOver = (e, name) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverItem !== name) setDragOverItem(name);
  };
  const handleDrop = (e, name) => {
    e.preventDefault();
    if (!draggedItem || draggedItem === name) return;
    const order = [...allProjects];
    const from = order.indexOf(draggedItem);
    const to = order.indexOf(name);
    order.splice(from, 1);
    order.splice(to, 0, draggedItem);
    setProjectOrder(order);
    setDraggedItem(null);
    setDragOverItem(null);
  };
  const handleDragEnd = () => { setDraggedItem(null); setDragOverItem(null); };

  const getPriorityColor = (p) => {
    if (p === 'High') return isDark ? 'text-red-400 bg-red-900/20 border-red-700/30' : 'text-red-600 bg-red-50 border-red-200';
    if (p === 'Medium') return isDark ? 'text-yellow-400 bg-yellow-900/20 border-yellow-700/30' : 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return isDark ? 'text-green-400 bg-green-900/20 border-green-700/30' : 'text-green-600 bg-green-50 border-green-200';
  };
  const getStatusColor = (s) => {
    if (s === 'Active') return isDark ? 'text-green-400 bg-green-900/20 border-green-700/30' : 'text-green-600 bg-green-50 border-green-200';
    if (s === 'Paused') return isDark ? 'text-yellow-400 bg-yellow-900/20 border-yellow-700/30' : 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return isDark ? 'text-blue-400 bg-blue-900/20 border-blue-700/30' : 'text-blue-600 bg-blue-50 border-blue-200';
  };
  const getExpectedROI = (name) => {
    const s = projectSettings[name];
    const val = getMetricValue(name, s.metricType || 'points');
    return s?.roiScenarios?.base && val > 0 ? val * s.roiScenarios.base : null;
  };
  const getTypeColor = (type) => {
    if (type === 'Trading') return isDark ? 'bg-orange-500/20 text-orange-300' : 'bg-orange-100 text-orange-600';
    if (type === 'Prediction Markets') return isDark ? 'bg-purple-500/20 text-purple-300' : 'bg-purple-100 text-purple-600';
    if (type === 'DeFi') return isDark ? 'bg-blue-500/20 text-blue-300' : 'bg-blue-100 text-blue-600';
    return isDark ? 'bg-slate-500/20 text-slate-300' : 'bg-gray-100 text-gray-600';
  };

  const inputClasses = `w-full border rounded px-3 py-2 focus:border-blue-500 focus:outline-none ${isDark ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`;

  const cardProps = {
    projectSettings, editingProject, editForm, setEditForm,
    cancelEdit, saveEdit, startEdit, deleteAirdrop, setSharingProject, weeklyData,
    isDark, colors, inputClasses, TYPE_OPTIONS, METRIC_OPTIONS,
    PRIORITY_OPTIONS, STATUS_OPTIONS, handleNumberChange, handleNumberBlur,
    getMetricLabel, getMetricValue, getTotalCosts, getExpectedROI,
    getStatusColor, getPriorityColor, getTypeColor, formatNumber
  };

  return (
    <div className="space-y-6">
      <div className={`${colors.bgCard} border ${colors.border} rounded-xl p-6 ${colors.cardShadow}`}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className={`text-2xl font-bold ${colors.textPrimary}`}>Airdrop Management</h3>
            {!isPro && <p className={`text-sm mt-1 ${colors.textSecondary}`}><Lock size={12} className="inline mr-1" />{allProjects.length}/{FREE_AIRDROP_LIMIT} airdrops (Free plan)</p>}
          </div>
          <button onClick={() => { setShowAddForm(!showAddForm); if (!showAddForm) resetForm(); }} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors">
            <Plus size={20} />Add Airdrop
          </button>
        </div>

        {showAddForm && (
          <div className={`rounded-lg p-5 border mb-6 ${isDark ? 'bg-slate-700/30 border-slate-600' : 'bg-gray-50 border-gray-200'}`}>
            <h4 className={`text-lg font-bold mb-4 ${colors.textPrimary}`}>New Airdrop</h4>
            {!isPro && allProjects.length >= FREE_AIRDROP_LIMIT && (
              <div className={`mb-4 p-4 rounded-lg border ${isDark ? 'bg-yellow-900/20 border-yellow-700/30 text-yellow-400' : 'bg-yellow-50 border-yellow-200 text-yellow-700'}`}>
                <p className="font-semibold">Free plan limit reached</p>
                <button onClick={openPricing} className="mt-2 px-4 py-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-lg text-sm font-semibold">Upgrade to Pro</button>
              </div>
            )}

            {showTemplates ? (
              <div className={`rounded-lg p-4 mb-5 border ${isDark ? 'bg-blue-900/10 border-blue-700/30' : 'bg-blue-50 border-blue-200'}`}>
                <div className="flex items-center gap-2 mb-3">
                  <Zap size={16} className={isDark ? 'text-blue-400' : 'text-blue-600'} />
                  <span className={`text-sm font-semibold ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>Quick Templates</span>
                  <span className={`text-xs ${colors.textSecondary}`}>— select one or fill in manually below</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {AIRDROP_TEMPLATES.map(t => (
                    <button key={t.id} onClick={() => applyTemplate(t)} className={`text-left rounded-lg p-3 border transition-all hover:scale-105 ${isDark ? 'bg-slate-800 border-slate-600 hover:border-blue-500' : 'bg-white border-gray-200 hover:border-blue-400'}`}>
                      <div className={`font-semibold text-sm mb-0.5 ${colors.textPrimary}`}>{t.name}</div>
                      <div className={`text-xs ${colors.textSecondary}`}>{t.description}</div>
                      <div className={`text-xs mt-1 font-medium ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>TGE: {t.data.tge}</div>
                      <div className="mt-2 flex flex-col gap-0.5">
                        <div className={`text-xs ${isDark ? 'text-red-400' : 'text-red-500'}`}>🐻 ${t.data.roiScenarios.bearish}/pt</div>
                        <div className={`text-xs ${isDark ? 'text-blue-400' : 'text-blue-500'}`}>📊 ${t.data.roiScenarios.base}/pt</div>
                        <div className={`text-xs ${isDark ? 'text-green-400' : 'text-green-500'}`}>🚀 ${t.data.roiScenarios.bullish}/pt</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className={`flex items-center gap-2 mb-4 text-sm ${colors.textSecondary}`}>
                <span>Template: <strong className={colors.textPrimary}>{AIRDROP_TEMPLATES.find(t => t.id === selectedTemplate)?.name}</strong></span>
                <button onClick={resetForm} className={`text-xs underline ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>Change template</button>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div><label className={`block text-sm mb-2 ${colors.textSecondary}`}>Airdrop Name *</label><input type="text" value={newAirdrop.name} onChange={(e) => setNewAirdrop({ ...newAirdrop, name: e.target.value })} placeholder="Hyperliquid" className={inputClasses} /></div>
              <div><label className={`block text-sm mb-2 ${colors.textSecondary}`}>Type</label><select value={newAirdrop.type} onChange={(e) => setNewAirdrop({ ...newAirdrop, type: e.target.value })} className={inputClasses}>{TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</select></div>
            </div>
            <div className={`rounded-lg p-4 mb-4 border ${isDark ? 'bg-purple-900/20 border-purple-700/30' : 'bg-purple-50 border-purple-200'}`}>
              <h5 className={`font-semibold mb-3 ${colors.textPrimary}`}>📏 Metric to Track</h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><label className={`block text-sm mb-2 ${colors.textSecondary}`}>Main Metric *</label><select value={newAirdrop.metricType} onChange={(e) => setNewAirdrop({ ...newAirdrop, metricType: e.target.value })} className={inputClasses}>{METRIC_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</select></div>
                {newAirdrop.metricType === 'custom' && <div><label className={`block text-sm mb-2 ${colors.textSecondary}`}>Custom Metric Name *</label><input type="text" value={newAirdrop.customMetricName} onChange={(e) => setNewAirdrop({ ...newAirdrop, customMetricName: e.target.value })} placeholder="e.g., LP Fees" className={inputClasses} /></div>}
              </div>
              <p className={`text-xs mt-2 ${colors.textSecondary}`}>💡 This determines what you'll track in "Add Activity" for this airdrop</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div><label className={`block text-sm mb-2 ${colors.textSecondary}`}>Farming Capital ($)</label><input type="text" value={newAirdrop.capital} onChange={(e) => handleNumberChange(setNewAirdrop, newAirdrop, 'capital', e.target.value)} onBlur={() => handleNumberBlur(setNewAirdrop, newAirdrop, 'capital')} placeholder="1.000" className={inputClasses} /></div>
              <div><label className={`block text-sm mb-2 ${colors.textSecondary}`}>Estimated TGE</label><input type="text" value={newAirdrop.tge} onChange={(e) => setNewAirdrop({ ...newAirdrop, tge: e.target.value })} placeholder="Q2 2026" className={inputClasses} /></div>
              <div><label className={`block text-sm mb-2 ${colors.textSecondary}`}>Start Date</label><input type="date" value={newAirdrop.startDate} onChange={(e) => setNewAirdrop({ ...newAirdrop, startDate: e.target.value })} className={inputClasses} /></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div><label className={`block text-sm mb-2 ${colors.textSecondary}`}>Priority</label><select value={newAirdrop.priority} onChange={(e) => setNewAirdrop({ ...newAirdrop, priority: e.target.value })} className={inputClasses}>{PRIORITY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</select></div>
              <div><label className={`block text-sm mb-2 ${colors.textSecondary}`}>Status</label><select value={newAirdrop.status} onChange={(e) => setNewAirdrop({ ...newAirdrop, status: e.target.value })} className={inputClasses}>{STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</select></div>
            </div>
            <div className="mb-4"><label className={`block text-sm mb-2 ${colors.textSecondary}`}>Notes / Strategy</label><textarea value={newAirdrop.notes} onChange={(e) => setNewAirdrop({ ...newAirdrop, notes: e.target.value })} placeholder="Ex: Trade at least 3 times per week..." rows="3" className={inputClasses} /></div>
            <div className="mb-4"><label className={`block text-sm mb-2 ${colors.textSecondary}`}>Ingresos ($) <span className="font-normal opacity-60">— reducen el coste neto del airdrop</span></label><input type="text" value={newAirdrop.ingresos} onChange={(e) => handleNumberChange(setNewAirdrop, newAirdrop, 'ingresos', e.target.value)} onBlur={() => handleNumberBlur(setNewAirdrop, newAirdrop, 'ingresos')} placeholder="0" className={inputClasses} /></div>
            <div className="flex gap-2">
              <button onClick={addAirdrop} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors">Save</button>
              <button onClick={() => { setShowAddForm(false); resetForm(); }} className={`px-4 py-2 rounded-lg transition-colors ${isDark ? 'bg-slate-600 hover:bg-slate-700 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}>Cancel</button>
            </div>
          </div>
        )}

        {allProjects.length === 0 ? (
          <div className={`text-center py-12 ${colors.textSecondary}`}>
            <AlertCircle size={48} className="mx-auto mb-4 opacity-50" />
            <p>No airdrops configured yet</p>
            <p className="text-sm mt-2">Add your first airdrop to start tracking</p>
          </div>
        ) : (
          <>
            <div className="sm:hidden -mx-2">
              <div className="flex gap-4 overflow-x-auto px-2 pb-4" style={{ scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch' }}>
                {allProjects.map(name => (
                  <MobileCard key={name} projectName={name} {...cardProps} />
                ))}
              </div>
              <div className="flex justify-center gap-1.5 mt-1">
                {allProjects.map((name) => (
                  <div key={name} className={`w-1.5 h-1.5 rounded-full ${isDark ? 'bg-slate-500' : 'bg-gray-300'}`} />
                ))}
              </div>
            </div>

            <div className="hidden sm:flex flex-col gap-4">
              {allProjects.length > 1 && (
                <p className={`text-xs flex items-center gap-1 ${colors.textSecondary}`}>
                  <GripVertical size={12} /> Drag cards to reorder
                </p>
              )}
              {allProjects.map(name => (
                <div
                  key={name}
                  draggable
                  onDragStart={(e) => handleDragStart(e, name)}
                  onDragOver={(e) => handleDragOver(e, name)}
                  onDrop={(e) => handleDrop(e, name)}
                  onDragEnd={handleDragEnd}
                >
                  <DesktopCard
                    projectName={name}
                    {...cardProps}
                    isDragging={draggedItem === name}
                    isDraggingOver={dragOverItem === name}
                  />
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {sharingProject && (
        <AirdropShareModal
          name={sharingProject}
          settings={projectSettings[sharingProject]}
          points={getMetricValue(sharingProject, projectSettings[sharingProject]?.metricType || 'points')}
          costs={getTotalCosts(weeklyData, sharingProject)}
          weeklyData={weeklyData}
          isDark={isDark}
          onClose={() => setSharingProject(null)}
        />
      )}
    </div>
  );
};